import {
  Button, Card, Descriptions, Empty, Input, Modal, Popconfirm, Rate, Select, Space, Spin, Table, Tabs, Tooltip, Typography,
} from 'antd';
import rideReviewApi from 'api/rideReviewApi';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import ToastHelper from 'general/helpers/ToastHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatReviewDate, getRideReviewErrorMessage, RATING_OPTIONS, REVIEW_DIRECTIONS,
} from './rideReviewsConstants';
import './RideReviews.scss';

const { Text, Title } = Typography;
const EMPTY_CRITERIA = { rating: '', driverEmail: '', customerEmail: '' };

const API_BY_DIRECTION = {
  driver: { get: rideReviewApi.getDriverReview, remove: rideReviewApi.removeDriverReview },
  customer: { get: rideReviewApi.getCustomerReview, remove: rideReviewApi.removeCustomerReview },
};

const ReviewParty = ({ value }) => {
  const label = value || '—';
  return (
    <Tooltip title={label === '—' ? null : label} placement="topLeft">
      <span className="ride-reviews__party">{label}</span>
    </Tooltip>
  );
};

const ReviewRating = ({ value }) => {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="ride-reviews__rating" aria-label={`${rating} trên 5 sao`}>
      <Rate disabled value={rating} />
      <span>{rating}/5</span>
    </div>
  );
};

const ReviewComment = ({ value }) => {
  const comment = String(value || '').trim();
  if (!comment) return <Text type="secondary">—</Text>;
  return (
    <Tooltip title={comment} placement="topLeft">
      <span className="ride-reviews__comment">{comment}</span>
    </Tooltip>
  );
};

const ReviewDateTime = ({ value }) => {
  if (!value) return <Text type="secondary">—</Text>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <Text type="secondary">—</Text>;
  return (
    <span className="ride-reviews__datetime">
      <strong>{date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}</strong>
      <small>{date.toLocaleDateString('vi-VN')}</small>
    </span>
  );
};

function ReviewDetailModal({ direction, reviewId, onClose, onRemoved }) {
  const meta = REVIEW_DIRECTIONS[direction];
  const api = API_BY_DIRECTION[direction];

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!reviewId || !api) return;
    setDetailLoading(true);
    try {
      const response = await api.get(reviewId);
      setDetail(response?.review || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRideReviewErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [reviewId, api]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const removeReview = async () => {
    if (!api || removing) return;
    setRemoving(true);
    try {
      await api.remove(reviewId);
      ToastHelper.showSuccess('Đã gỡ đánh giá');
      onRemoved();
      onClose();
    } catch (error) {
      ToastHelper.showError(getRideReviewErrorMessage(error));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Modal
      title={detail ? `Đánh giá #${detail.reviewId}` : 'Chi tiết đánh giá'}
      open={Boolean(reviewId)}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={detailLoading}>
        {!detail && !detailLoading ? <Empty description="Không tải được đánh giá" /> : detail && meta && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Chuyến">{detail.tripCode}</Descriptions.Item>
              <Descriptions.Item label={meta.reviewerLabel}>
                {detail.customer?.fullname || detail.driver?.fullname || '—'}
                {' · '}
                {detail.customer?.phone || detail.customer?.email || detail.driver?.phone || detail.driver?.email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={meta.subjectLabel}>
                {detail.driver?.fullname || detail.customer?.fullname || '—'}
                {' · '}
                {detail.driver?.phone || detail.driver?.email || detail.customer?.phone || detail.customer?.email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Số sao"><ReviewRating value={detail.rating} /></Descriptions.Item>
              <Descriptions.Item label="Nhận xét"><ReviewComment value={detail.comment} /></Descriptions.Item>
              <Descriptions.Item label="Tạo lúc">{formatReviewDate(detail.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Cập nhật lúc">{formatReviewDate(detail.updatedAt)}</Descriptions.Item>
            </Descriptions>
            <Popconfirm
              title="Gỡ đánh giá này?"
              description="Đánh giá vi phạm chính sách sẽ bị gỡ khỏi hệ thống."
              okText="Gỡ"
              okButtonProps={{ danger: true, loading: removing }}
              cancelText="Hủy"
              onConfirm={removeReview}
            >
              <Button danger>Gỡ đánh giá</Button>
            </Popconfirm>
          </Space>
        )}
      </Spin>
    </Modal>
  );
}

// One table shared by both directions — only the API call and column labels
// ("Tài xế"/"Khách hàng" first) differ between "Khách -> Tài xế" and
// "Tài xế -> Khách". Clicking a row opens the shared detail popup instead
// of navigating away.
function ReviewDirectionPanel({
  direction, list, subjectLabel, reviewerLabel, onOpenDetail,
}) {
  const [filter, setFilter] = useState({ page: 1, pageSize: 20 });
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await list(filter);
      setItems(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      ToastHelper.showError(getRideReviewErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter, list]);

  useEffect(() => { load(); }, [load]);

  const applyCriteria = () => setFilter((value) => ({ ...value, ...criteria, page: 1 }));
  const resetCriteria = () => {
    setCriteria(EMPTY_CRITERIA);
    setFilter((value) => ({ page: 1, pageSize: value.pageSize }));
  };

  const columns = useMemo(() => [
    { title: 'Mã đánh giá', dataIndex: 'reviewId', width: 105, render: (value) => <Text strong>#{value}</Text> },
    {
      title: 'Mã chuyến', dataIndex: 'tripCode', width: 190,
      render: (value) => <ReviewParty value={value} />,
    },
    {
      title: reviewerLabel,
      width: 225,
      render: (_, row) => <ReviewParty value={row.customer?.fullname || row.driver?.fullname} />,
    },
    {
      title: subjectLabel,
      width: 225,
      render: (_, row) => <ReviewParty value={row.driver?.fullname || row.customer?.fullname} />,
    },
    { title: 'Đánh giá', dataIndex: 'rating', width: 150, render: (value) => <ReviewRating value={value} /> },
    { title: 'Nhận xét', dataIndex: 'comment', width: 260, render: (value) => <ReviewComment value={value} /> },
    { title: 'Tạo lúc', dataIndex: 'createdAt', width: 130, render: (value) => <ReviewDateTime value={value} /> },
    {
      title: '',
      width: 90,
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Button className="ride-reviews__detail-button" type="link" onClick={() => onOpenDetail(direction, row.reviewId)}>Chi tiết</Button>
      ),
    },
  ], [reviewerLabel, subjectLabel, direction, onOpenDetail]);

  return (
    <>
      <Space className="ride-reviews__filters" wrap size={[12, 12]}>
        <Select
          value={criteria.rating}
          options={RATING_OPTIONS}
          style={{ width: 160 }}
          onChange={(rating) => setCriteria((value) => ({ ...value, rating }))}
        />
        <Input
          value={criteria.driverEmail}
          placeholder="Email tài xế"
          allowClear
          style={{ width: 220 }}
          onChange={(event) => setCriteria((value) => ({ ...value, driverEmail: event.target.value }))}
          onPressEnter={applyCriteria}
        />
        <Input
          value={criteria.customerEmail}
          placeholder="Email khách hàng"
          allowClear
          style={{ width: 220 }}
          onChange={(event) => setCriteria((value) => ({ ...value, customerEmail: event.target.value }))}
          onPressEnter={applyCriteria}
        />
        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={applyCriteria}>Lọc</Button>
        <Button onClick={resetCriteria}>Xóa lọc</Button>
        <Button onClick={load} loading={loading}>Làm mới</Button>
      </Space>
      <Table
        className="ride-reviews__table"
        rowKey="reviewId"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 1375 }}
        tableLayout="fixed"
        locale={{ emptyText: 'Không có dữ liệu' }}
        pagination={{
          current: filter.page,
          pageSize: filter.pageSize,
          total,
          showSizeChanger: true,
          onChange: (page, pageSize) => setFilter((value) => ({ ...value, page, pageSize })),
        }}
        onRow={(row) => ({ onDoubleClick: () => onOpenDetail(direction, row.reviewId) })}
      />
    </>
  );
}

function RideReviews() {
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const openDetail = useCallback((direction, reviewId) => setSelected({ direction, reviewId }), []);
  const closeDetail = useCallback(() => setSelected(null), []);
  const bumpRefreshKey = useCallback(() => setRefreshKey((value) => value + 1), []);

  const driverReviewsPanel = useMemo(() => ({
    direction: 'driver',
    list: rideReviewApi.getDriverReviews,
    subjectLabel: 'Tài xế',
    reviewerLabel: 'Khách hàng đánh giá',
    onOpenDetail: openDetail,
  }), [openDetail]);
  const customerReviewsPanel = useMemo(() => ({
    direction: 'customer',
    list: rideReviewApi.getCustomerReviews,
    subjectLabel: 'Khách hàng',
    reviewerLabel: 'Tài xế đánh giá',
    onOpenDetail: openDetail,
  }), [openDetail]);

  return (
    <KT01BaseLayout isFullScreen>
      <div className="container-fluid px-4 my-4 ride-reviews">
        <Card className="ride-reviews__card">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <Title level={4} className="mb-0">Đánh giá xe ghép</Title>
              <Text type="secondary">Chỉ xem, gỡ đánh giá vi phạm chính sách</Text>
            </div>
          </div>
          <Tabs
            items={[
              {
                key: 'driver',
                label: 'Khách đánh giá tài xế',
                children: <ReviewDirectionPanel key={refreshKey} {...driverReviewsPanel} />,
              },
              {
                key: 'customer',
                label: 'Tài xế đánh giá khách',
                children: <ReviewDirectionPanel key={refreshKey} {...customerReviewsPanel} />,
              },
            ]}
          />
        </Card>
      </div>
      <ReviewDetailModal
        direction={selected?.direction}
        reviewId={selected?.reviewId}
        onClose={closeDetail}
        onRemoved={bumpRefreshKey}
      />
    </KT01BaseLayout>
  );
}

export default RideReviews;
